import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Switch } from '@neo/primitives/switch'
import { Textarea } from '@neo/primitives/textarea'
import { ChevronRight, Sparkles, Trash, Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { RAgentJudge, RJudgeScope } from 'v2/types/api/agent-observability'
import {
  useCreateMetricMutation,
  useDeleteMetricMutation,
  useImproveDescriptionMutation,
  useToggleMetricMutation,
  useUpdateMetricMutation,
} from './metrics-tab.queries'

export interface MetricDraftSeed {
  display_name: string
  description: string
  scope: RJudgeScope
}

export interface LastTestSummary {
  calls: number
  failed: number
}

interface MetricJudgeCardProps {
  /** null renders a draft card (blank, or seeded from `seed`). */
  judge: RAgentJudge | null
  agentId: string
  expanded: boolean
  onToggleExpand: () => void
  onTest?: () => void
  seed?: MetricDraftSeed
  /** Unique key for a draft card so its test-ids don't collide. */
  draftKey?: string
  /** A calibration result pushes a refined description in for the user to review. */
  calibratedDescription?: string
  lastTest?: LastTestSummary
  onDraftSaved?: (judgeId: string) => void
  onDiscardDraft?: () => void
}

export const MetricJudgeCard = ({
  judge,
  agentId,
  expanded,
  onToggleExpand,
  onTest,
  seed,
  draftKey,
  calibratedDescription,
  lastTest,
  onDraftSaved,
  onDiscardDraft,
}: MetricJudgeCardProps) => {
  const [name, setName] = useState(judge?.display_name ?? seed?.display_name ?? '')
  const [description, setDescription] = useState(judge?.description ?? seed?.description ?? '')
  // Scope is fixed to per-call for this version — no in-card toggle. Existing judges keep their
  // saved scope; new metrics default to conversation (per call).
  const scope: RJudgeScope = judge?.scope ?? seed?.scope ?? 'conversation'
  const [preImprove, setPreImprove] = useState<string | null>(null)

  const createMutation = useCreateMetricMutation(agentId)
  const updateMutation = useUpdateMetricMutation(agentId)
  const deleteMutation = useDeleteMetricMutation(agentId)
  const toggleMutation = useToggleMetricMutation(agentId)
  const improveMutation = useImproveDescriptionMutation()

  useEffect(() => {
    if (calibratedDescription) setDescription(calibratedDescription)
  }, [calibratedDescription])

  const isDraft = judge === null
  const cardKey = judge?.id ?? draftKey ?? 'draft'
  const trimmedName = name.trim()
  const trimmedDescription = description.trim()
  const dirty = isDraft
    ? trimmedName !== '' || trimmedDescription !== ''
    : trimmedName !== judge.display_name ||
      trimmedDescription !== judge.description ||
      scope !== judge.scope
  const canSave = dirty && trimmedName !== '' && trimmedDescription !== ''
  const isSaving = createMutation.isPending || updateMutation.isPending

  const handleSave = () => {
    const values = { display_name: trimmedName, description: trimmedDescription, scope }
    if (isDraft) {
      createMutation.mutate(values, { onSuccess: (created) => onDraftSaved?.(created.id) })
    } else {
      updateMutation.mutate({ id: judge.id, values })
    }
  }

  const handleImprove = () => {
    if (!trimmedDescription && !trimmedName) return
    setPreImprove(description)
    improveMutation.mutate(
      { name: trimmedName, description: trimmedDescription, scope },
      {
        onSuccess: (res) => {
          setDescription(res.description)
          if (res.display_name) setName(res.display_name)
        },
      },
    )
  }

  const statusText = lastTest
    ? `Last run: ${lastTest.calls} ${lastTest.calls === 1 ? 'call' : 'calls'}, ${lastTest.failed} failed`
    : 'Not tested yet'

  return (
    <div className="bg-bg2 border border-st2 rounded-xl p-3 flex flex-col gap-3">
      <div className="w-full flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex items-center gap-3 text-left flex-1 min-w-0"
          data-testid={`btn-metric-card-${cardKey}`}
        >
          <ChevronRight
            size={16}
            className={`text-tertiary shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <div className="min-w-0 flex flex-col flex-1">
            <span className="text-p-500 truncate">
              {isDraft ? trimmedName || 'New metric' : judge.display_name}
            </span>
            {!expanded && (
              <span className="text-s-400 text-secondary truncate">{trimmedDescription}</span>
            )}
          </div>
        </button>
        {!isDraft && (
          // A metric runs only when BOTH the registry switch (judge.enabled) and the agent
          // mapping are on; mapping_enabled alone would read "on" after a registry-level
          // disable. Turning on re-enables both (useToggleMetricMutation).
          <Switch
            value={judge.enabled && judge.mapping_enabled}
            loading={toggleMutation.isPending}
            onChange={(checked) => toggleMutation.mutate({ judge, enabled: checked })}
            aria-label={
              judge.enabled && judge.mapping_enabled ? 'Turn off metric' : 'Turn on metric'
            }
            data-testid={`btn-toggle-metric-${cardKey}`}
            className="shrink-0"
          />
        )}
      </div>

      {expanded && (
        <div className="flex flex-col gap-3">
          <Input
            label="Name"
            placeholder="e.g. Caller was greeted by name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="input-metric-name"
          />
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-s-500">Description</span>
              <div className="flex items-center gap-1">
                {preImprove !== null && (
                  <button
                    type="button"
                    className="text-tertiary hover:text-primary p-1"
                    onClick={() => {
                      setDescription(preImprove)
                      setPreImprove(null)
                    }}
                    aria-label="Undo AI improvement"
                    title="Undo AI improvement"
                    data-testid="btn-improve-undo"
                  >
                    <Undo2 size={14} />
                  </button>
                )}
                <ButtonWithLoader
                  variant="ghost"
                  size="sm"
                  loading={improveMutation.isPending}
                  disabled={!trimmedDescription && !trimmedName}
                  onClick={handleImprove}
                  className="gap-1.5 text-primary"
                  data-testid="btn-improve-metric"
                >
                  <Sparkles size={14} />
                  Improve with AI
                </ButtonWithLoader>
              </div>
            </div>
            <Textarea
              placeholder="Describe, in plain language, what must be true on a call for this metric to pass"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="bg-background"
              data-testid="input-metric-description"
            />
          </div>

          <div className="flex items-center justify-between border-t border-st2 pt-3">
            <div className="flex items-center gap-3">
              {isDraft ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onDiscardDraft}
                  data-testid="btn-discard-metric"
                >
                  <Trash size={16} className="text-destructive" />
                </Button>
              ) : (
                <ConfirmPopup
                  title="Delete metric?"
                  description={`"${judge.display_name}" and its results mapping will be removed. This cannot be undone.`}
                  okButtonProps={{
                    btnTxt: 'Delete',
                    variant: 'destructive',
                    onClick: () => deleteMutation.mutate(judge.id),
                  }}
                >
                  <Button variant="ghost" size="icon" data-testid="btn-delete-metric">
                    <Trash size={16} className="text-destructive" />
                  </Button>
                </ConfirmPopup>
              )}
              {!isDraft && (
                <span className="text-s-400 text-tertiary" data-testid="text-metric-status">
                  {statusText}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ButtonWithLoader
                variant="outline"
                size="sm"
                loading={isSaving}
                disabled={!canSave}
                onClick={handleSave}
                data-testid="btn-save-metric"
              >
                Save
              </ButtonWithLoader>
              <Button
                variant="outline"
                size="sm"
                disabled={isDraft}
                onClick={onTest}
                data-testid="btn-test-metric"
              >
                Test metric
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
